import { FormattedMessage } from 'react-intl';
import { StripeElementsOptions } from '@stripe/stripe-js';
import { useCohesionConfig } from 'helpers/eventing';
import { getPriceDetailsFromCart } from 'helpers/priceHelpers';
import {
  getHasCartLoadFailure,
  getIsSessionInvalid,
  useAppSelector,
} from 'store';
import { useCart } from 'frontastic';
import { CheckoutSkeleton } from '../skeletons';
import { CheckoutProps } from '../types';
import { FreeOrderCheckoutForm } from './checkout-form/free-order-form';
import {
  Alert,
  CheckoutForm,
  EmptyCartCard,
  OrderDetails,
  OrderSummary,
  PaymentMethodSelector,
  ProductLineItem,
  SessionInvalidAlertModal,
  StripeFormProvider,
} from '.';

export const CheckoutControl = ({ stripePublishableKey }: CheckoutProps) => {
  const hasCartLoadFailure = useAppSelector(getHasCartLoadFailure);
  const isSessionInvalid = useAppSelector(getIsSessionInvalid);
  const { data, isEmpty, isLoading, redeemDiscountCode, removeDiscountCode } =
    useCart();

  const lineItems = data?.lineItems ?? [];
  const priceDetails = getPriceDetailsFromCart(data);
  const isCreditPurchase = lineItems.some(
    (item) => item.variant.attributes?.mode === 'credit',
  );

  const stripeOptions = {
    mode: 'payment',
    amount: priceDetails.cartTotal,
    currency: priceDetails.currencyCode?.toLowerCase(),
    paymentMethodCreation: 'manual',
    paymentMethodTypes: ['card'],
  } satisfies StripeElementsOptions;

  const isEmptyCart = isEmpty || lineItems.length === 0;
  const shouldHideAlert = isSessionInvalid && !hasCartLoadFailure;
  const purchaseType = lineItems.some(({ bundleId }) => bundleId)
    ? 'program'
    : lineItems[0]?.variant?.attributes?.mode;
  const isFreeOrder = priceDetails.cartTotal === 0;

  useCohesionConfig('checkout');

  const renderCheckout = () => (
    <div className="row">
      <section className="col-md-5 pr-md-5 col-cart-summary">
        <h1 className="sr-only">
          <FormattedMessage
            id="payment.checkout.main.heading"
            defaultMessage="Payment"
            description="Heading For Checkout for Screen Readers"
          />
        </h1>
        <div>
          <h2 className="h5">
            <FormattedMessage
              id="payment.product.line.item.purchase.cart.heading"
              defaultMessage="In Your Cart"
              description="Heading of the cart in product details section"
            />
          </h2>
          <p>
            <FormattedMessage
              id="payment.product.line.item.purchase.cart.subheading"
              defaultMessage="Your purchase contains the following:"
              description="Subheading of the cart in product details section"
            />
          </p>
          <div className="list-wrapper">
            {lineItems.map((item) => (
              <ProductLineItem key={item.lineItemId} item={item} />
            ))}
          </div>
        </div>
        <OrderSummary
          isCreditPurchase={isCreditPurchase}
          priceDetails={priceDetails}
          onApplyCoupon={redeemDiscountCode(data!.cartId!)}
          onRemoveCoupon={removeDiscountCode(data!.cartId!)}
        />
        <OrderDetails purchaseType={purchaseType} />
      </section>

      <section className="col-md-7 pl-md-5">
        {!isFreeOrder && <PaymentMethodSelector />}
        {!isFreeOrder && (
          <StripeFormProvider
            stripePublishableKey={stripePublishableKey}
            options={stripeOptions}
          >
            <CheckoutForm
              cartID={data!.cartId!}
              priceDetails={priceDetails}
              lineItems={lineItems}
            />
          </StripeFormProvider>
        )}
        {isFreeOrder && (
          <FreeOrderCheckoutForm
            cartID={data!.cartId!}
            priceDetails={priceDetails}
            lineItems={lineItems}
          />
        )}
      </section>
    </div>
  );

  if (isLoading) {
    return <CheckoutSkeleton />;
  }

  return (
    <div className="container-fluid py-5">
      <SessionInvalidAlertModal isOpen={isSessionInvalid} />
      {shouldHideAlert ? null : <Alert />}
      {hasCartLoadFailure ? null : isEmptyCart ? (
        <EmptyCartCard />
      ) : (
        renderCheckout()
      )}
    </div>
  );
};
